import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { initPostHog } from './config/posthog';

// Strip all console.log/debug/info output in production builds.
// console.error and console.warn are preserved so Sentry breadcrumbs and
// legitimate runtime warnings still surface in monitoring tools.
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
}

// Initialise Sentry before React renders — captures errors from the first frame.
// Must be called after all imports but before any component is rendered.
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT ?? process.env.NODE_ENV,
  enabled: !!process.env.REACT_APP_SENTRY_DSN && process.env.NODE_ENV !== 'test',
  tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Mask all text and block all media in session replays (GDPR-safe)
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],

  // Session replays: 10% of sessions, 100% on error sessions
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  beforeSend(event) {
    // In @sentry/react v10, event.breadcrumbs is Breadcrumb[] directly (not
    // { values?: Breadcrumb[] }).  Map the array in-place to strip any
    // accidental token/password values from breadcrumb URLs.
    if (Array.isArray(event.breadcrumbs)) {
      event.breadcrumbs = event.breadcrumbs.map((b) => {
        if (b.data?.url?.includes('token') || b.data?.url?.includes('password')) {
          return { ...b, data: { ...b.data, url: '[Filtered]' } };
        }
        return b;
      });
    }
    return event;
  },
});

// Product analytics — pageviews + autocapture out of the box; identify()
// is called from AuthContext once a user is known, so events tie to real
// accounts instead of staying anonymous.
initPostHog();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Every page is React.lazy()-loaded by chunk filename hash. Right after a
// deploy, a browser tab that already has the old index.html cached will try
// to fetch a chunk by its old hash, get a 404 (the new build renamed it),
// and throw — surfacing as a generic "Something went wrong" even though the
// fix is just "reload to pick up the new index.html". Detect that specific
// failure and reload once automatically instead of making the user do it;
// the sessionStorage flag stops a genuinely broken chunk from reload-looping.
const isChunkLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  return /loading chunk|dynamically imported module|importing a module script failed/i.test(message);
};

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      onError={(error) => {
        if (isChunkLoadError(error) && !sessionStorage.getItem('chunk-reload-attempted')) {
          sessionStorage.setItem('chunk-reload-attempted', '1');
          window.location.reload();
        }
      }}
      fallback={({ error, resetError }) => (
        <div style={{ padding: 32, textAlign: 'center', fontFamily: 'sans-serif' }}>
          <h2>Something went wrong</h2>
          <p style={{ color: '#666', marginBottom: 24 }}>
            Our team has been notified. Please try refreshing the page.
          </p>
          <button
            onClick={resetError}
            style={{
              padding: '10px 24px',
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              marginRight: 12,
            }}
          >
            Try again
          </button>
          <button
            onClick={() => window.location.assign('/')}
            style={{
              padding: '10px 24px',
              background: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Go to home
          </button>
        </div>
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

reportWebVitals();
